import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AnalystClarification } from 'src/app/models/analyst-output.model';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

interface ClarificationAnswer {
  selectedOptions: string[];
  freeText: string;
}

function emptyAnswer(): ClarificationAnswer {
  return { selectedOptions: [], freeText: '' };
}

@Component({
  standalone: false,
  selector: 'app-analyst-clarification',
  templateUrl: './analyst-clarification.component.html',
  styleUrls: ['./analyst-clarification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalystClarificationComponent implements OnChanges {
  @Input() clarifications: AnalystClarification[] = [];
  @Input() disabled = false;
  @Input() answered = false;
  @Input() answeredText: string | null = null;
  @Output() optionSelected = new EventEmitter<string>();

  readonly trackByIndex = trackByIndex;

  currentIndex = 0;
  dismissed = false;

  private answers: ClarificationAnswer[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clarifications']) {
      this.answers = this.clarifications.map(() => emptyAnswer());
      this.currentIndex = 0;
      this.dismissed = false;
    }
  }

  get currentClarification(): AnalystClarification | undefined {
    return this.clarifications[this.currentIndex];
  }

  get currentAnswer(): ClarificationAnswer {
    return this.answers[this.currentIndex] ?? emptyAnswer();
  }

  get currentSelectedCount(): number {
    const answer = this.currentAnswer;
    return answer.selectedOptions.length + (answer.freeText.trim() ? 1 : 0);
  }

  get canSubmit(): boolean {
    return this.answers.some((answer) => this.isAnswered(answer));
  }

  get isLastQuestion(): boolean {
    return this.currentIndex === this.clarifications.length - 1;
  }

  get skipButtonLabel(): string {
    const hasAnswerElsewhere = this.answers.some((answer, index) => index !== this.currentIndex && this.isAnswered(answer));
    return this.isLastQuestion && hasAnswerElsewhere ? 'Skip & Submit' : 'Skip';
  }

  isOptionSelected(option: string): boolean {
    return this.currentAnswer.selectedOptions.includes(option);
  }

  toggleOption(option: string): void {
    if (this.disabled) {
      return;
    }
    const answer = this.answers[this.currentIndex];
    answer.selectedOptions = answer.selectedOptions.includes(option)
      ? answer.selectedOptions.filter((selected) => selected !== option)
      : [...answer.selectedOptions, option];
  }

  setFreeText(value: string): void {
    if (this.disabled) {
      return;
    }
    this.answers[this.currentIndex] = { ...this.answers[this.currentIndex], freeText: value };
  }

  prev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
  }

  next(): void {
    if (this.currentIndex < this.clarifications.length - 1) {
      this.currentIndex += 1;
    }
  }

  skip(): void {
    if (this.disabled) {
      return;
    }
    this.answers[this.currentIndex] = emptyAnswer();
    if (this.currentIndex < this.clarifications.length - 1) {
      this.next();
      return;
    }
    this.submitOrDismiss();
  }

  onClose(): void {
    this.dismissed = true;
  }

  onSubmit(): void {
    if (this.disabled) {
      return;
    }
    this.submitOrDismiss();
  }

  private submitOrDismiss(): void {
    const payload = this.buildSubmitPayload();
    if (!payload) {
      this.dismissed = true;
      return;
    }
    this.optionSelected.emit(payload);
    this.answers = this.clarifications.map(() => emptyAnswer());
  }

  private buildSubmitPayload(): string | null {
    const answered = this.clarifications
      .map((clarification, index) => ({ question: clarification.question, answer: this.answerText(index) }))
      .filter((entry) => entry.answer.length > 0);

    if (!answered.length) {
      return null;
    }
    if (answered.length === 1 && this.clarifications.length === 1) {
      return answered[0].answer;
    }
    return answered.map((entry) => `Q: ${entry.question}\nA: ${entry.answer}`).join('\n\n');
  }

  private answerText(index: number): string {
    const answer = this.answers[index];
    const freeText = answer.freeText.trim();
    return [...answer.selectedOptions, ...(freeText ? [freeText] : [])].join(', ');
  }

  private isAnswered(answer: ClarificationAnswer): boolean {
    return answer.selectedOptions.length > 0 || answer.freeText.trim().length > 0;
  }
}
